import { defineStore } from 'pinia';
import { api, ApiError } from '@/lib/api';
import { impersonateUser, stopImpersonating } from '@/lib/users';
import { applyUserLocale, type UiLocale } from '@/i18n';

const ACCESS_KEY  = 'cms-admin.access';
const REFRESH_KEY = 'cms-admin.refresh';

/** The /me request in flight, and the one queued behind it (see load()). */
let inflight: Promise<void> | null = null;
let queued: Promise<void> | null = null;

export interface ActiveWebsite {
    id: number;
    code: string;
    name: string;
    theme: string;
    defaultLocale: string;
    locales: string[];
    active: boolean;
    /** Hostname flagged canonical, or null when none is marked. */
    canonicalHost: string | null;
    /** Resolver-computed effective state per registered module id. */
    modules: Record<string, boolean>;
}

/** Minimal website summary for the switcher + sidebar gating. */
export interface AccessibleWebsite {
    id: number;
    code: string;
    name: string;
}

/** The original super behind an impersonated session, if any. */
export interface Impersonator {
    id: number;
    email: string;
}

export interface MeUser {
    id: number;
    email: string;
    roles: string[];
    super: boolean;
    /** Websites the user can switch between. Super sees everything; admins/editors see memberships. */
    accessibleWebsites: AccessibleWebsite[];
    activeWebsite: ActiveWebsite | null;
    /** Admin UI language the user picked; null = never picked, follows the browser. */
    locale: string | null;
    /**
     * Non-null when this session is an impersonation: the super who
     * started it. Persisted via the JWT claim, so it survives reloads.
     */
    impersonator: Impersonator | null;
}

// API wire shapes are snake_case (cms-api emits snake_case). The store
// maps them to the camelCase internal models above at the boundary, so
// components keep reading me.activeWebsite / .accessibleWebsites etc.
interface LoginResponse {
    user: { id: number; email: string; roles: string[] };
    access_token: string;
    refresh_token: string;
}

interface RefreshResponse {
    access_token: string;
    refresh_token: string;
}

/** Wire shape of the active website (snake_case) before mapping. */
interface ApiActiveWebsite {
    id: number;
    code: string;
    name: string;
    theme: string;
    default_locale: string;
    locales: string[];
    active: boolean;
    canonical_host: string | null;
    modules: Record<string, boolean>;
}

interface MeResponse {
    id: number;
    email: string;
    super: boolean;
    roles: string[];
    accessible_websites: AccessibleWebsite[];
    active_website: ApiActiveWebsite | null;
    locale?: string | null;
    /** Present only during an impersonated session. */
    impersonator?: Impersonator | null;
}

export const useMeStore = defineStore('me', {
    state: () => ({
        me: null as MeUser | null,
        accessToken: localStorage.getItem(ACCESS_KEY) ?? '',
        refreshToken: localStorage.getItem(REFRESH_KEY) ?? '',
        loaded: false,
        loading: false,
        error: null as string | null,
    }),

    getters: {
        isAuthenticated: (s) => s.me !== null,
        isSuper: (s) => s.me?.super ?? false,
        /**
         * Admin-or-better: super OR ROLE_ADMIN explicitly. Used to gate
         * the Websites/Users sidebar entries — admins manage their sites,
         * super manages every site.
         */
        isAdmin: (s) => s.me?.super === true || (s.me?.roles ?? []).includes('ROLE_ADMIN'),
        /** True when the current session was started by a super impersonating this user. */
        isImpersonating: (s) => s.me?.impersonator != null,
        /** True when the user has more than one website to switch between. */
        canSwitchWebsite: (s) => (s.me?.accessibleWebsites ?? []).length > 1,
        /**
         * Whether a module is effectively enabled on the active website.
         * Use for nav gating (`v-if="me.isModuleEnabled('catalog')"`).
         * Falls back to false when no website is active or the id isn't
         * registered, matching the resolver's behaviour server-side.
         */
        isModuleEnabled: (s) => (id: string): boolean =>
            s.me?.activeWebsite?.modules?.[id] === true,
    },

    actions: {
        /**
         * Boot-time call, and the reload after anything that changes what
         * the sidebar or the website switcher show (modules, a website
         * added or renamed). Hits /api/admin/me with the persisted access
         * token (if any). 401 → router redirects to /login. The api.ts
         * 401 interceptor will silently refresh once before giving up,
         * so a page reload after a long pause still works.
         *
         * A call made while one is running asks for newer data than that
         * one will bring (a website saved in between), so it runs once
         * more afterwards instead of being dropped. Never throws.
         */
        load(): Promise<void> {
            if (inflight) {
                queued ??= inflight.then(() => {
                    queued = null;
                    return this.load();
                });
                return queued;
            }
            inflight = this.fetchMe().finally(() => {
                inflight = null;
            });
            return inflight;
        },

        /** One /me round trip into state; load() is the entry point. */
        async fetchMe() {
            this.loading = true;
            this.error = null;
            try {
                if (!this.accessToken) {
                    // No token at all — fast-path the 401 state without
                    // round-tripping the server.
                    this.me = null;
                    return;
                }
                const data = await api<MeResponse>('/api/admin/me');
                const aw = data.active_website;
                this.me = {
                    id: data.id,
                    email: data.email,
                    roles: data.roles ?? [],
                    super: data.super,
                    accessibleWebsites: data.accessible_websites ?? [],
                    activeWebsite: aw ? {
                        id: aw.id,
                        code: aw.code,
                        name: aw.name,
                        theme: aw.theme,
                        defaultLocale: aw.default_locale,
                        locales: aw.locales,
                        active: aw.active,
                        canonicalHost: aw.canonical_host ?? null,
                        modules: aw.modules,
                    } : null,
                    locale: data.locale ?? null,
                    impersonator: data.impersonator ?? null,
                };
                applyUserLocale(this.me.locale);
            } catch (err) {
                if (err instanceof ApiError && err.status === 401) {
                    this.clearTokens();
                    this.me = null;
                } else {
                    this.error = err instanceof Error ? err.message : String(err);
                }
            } finally {
                this.loaded = true;
                this.loading = false;
            }
        },

        /**
         * Store the user's admin language on their account and switch to it
         * now. Null goes back to following the browser.
         */
        async saveLocale(code: UiLocale | null) {
            const res = await api<{ locale: string | null }>('/api/admin/me', { method: 'PATCH', body: { locale: code } });
            if (this.me) this.me.locale = res.locale;
            applyUserLocale(res.locale);
        },

        async login(email: string, password: string) {
            const data = await api<LoginResponse>('/admin/login', {
                method: 'POST',
                body: { email, password },
                noAuth: true,
            });
            this.setTokens(data.access_token, data.refresh_token);
            // Reload /me to pick up activeWebsite + expanded roles.
            this.loaded = false;
            await this.load();
        },

        /**
         * Adopt a token pair issued by some other login flow (e.g. passkey).
         * Same post-login plumbing as email/password — reload /me, etc.
         */
        async adoptTokens(access: string, refresh: string) {
            this.setTokens(access, refresh);
            this.loaded = false;
            await this.load();
        },

        /**
         * Mint a fresh access+refresh pair using the stored refresh token.
         * Returns true on success, false if the refresh token was invalid
         * or expired (caller should clear state and redirect to /login).
         */
        async refresh(): Promise<boolean> {
            if (!this.refreshToken) return false;
            try {
                const data = await api<RefreshResponse>('/admin/refresh', {
                    method: 'POST',
                    body: { refresh_token: this.refreshToken },
                    noAuth: true,
                });
                this.setTokens(data.access_token, data.refresh_token);
                return true;
            } catch {
                return false;
            }
        },

        async logout() {
            // Stateless: server has no record to revoke. Just drop client state.
            this.clearTokens();
            this.me = null;
        },

        /**
         * Start impersonating user `id` (super-only server-side). Swaps in
         * the impersonated token pair and reloads /me, which will now carry
         * the `impersonator` field. Caller redirects afterwards.
         */
        async impersonate(id: number) {
            const tokens = await impersonateUser(id);
            await this.adoptTokens(tokens.access_token, tokens.refresh_token);
        },

        /**
         * Leave an impersonated session: exchange the impersonated token
         * for a fresh pair back as the original super, then reload /me.
         */
        async stopImpersonating() {
            const tokens = await stopImpersonating();
            await this.adoptTokens(tokens.access_token, tokens.refresh_token);
        },

        setTokens(access: string, refresh: string) {
            this.accessToken = access;
            this.refreshToken = refresh;
            localStorage.setItem(ACCESS_KEY, access);
            localStorage.setItem(REFRESH_KEY, refresh);
        },

        clearTokens() {
            this.accessToken = '';
            this.refreshToken = '';
            localStorage.removeItem(ACCESS_KEY);
            localStorage.removeItem(REFRESH_KEY);
        },
    },
});
